/**
 * APDatePicker Component
 *
 * APTextField + APCalendar combo that uses the APSelect overlay pattern.
 * Emits both Date value and formatted string using shared DateFormats utilities.
 */

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { format, isValid, parse } from 'date-fns';

import { APIconButton } from '@shared/components';
import APTextField from '@shared/components/atoms/text-field';
import { useTranslate } from '@shared/hooks/use-translate';
import { DateFormats, formatDateAs } from '@shared/utils/date';

import type { IAPDatePickerChangeEvent, IAPDatePickerProps } from './types';

import { APCalendar } from '../calendar';

import './styles.scss';

export const APDatePicker = forwardRef<HTMLInputElement, IAPDatePickerProps>(
  (
    {
      value,
      defaultValue,
      format: dateFormat = DateFormats.YearMonthAndDay,
      texts,
      minDate,
      maxDate,
      calendarProps,
      onChange,
      disabled,
      clearable = true,
      rightIcon,
      ...textFieldProps
    },
    ref
  ) => {
    const translate = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [internalDate, setInternalDate] = useState<Date | null>(defaultValue ?? null);
    const [month, setMonth] = useState<Date>(() => value ?? defaultValue ?? new Date());
    const containerRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuPosition, setMenuPosition] = useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);

    const selectedDate = value ?? internalDate;

    const formattedValue = useMemo(
      () => (selectedDate ? formatDateAs(selectedDate, dateFormat) : ''),
      [selectedDate, dateFormat]
    );

    const emitChange = useCallback(
      (nextDate: Date | null) => {
        const event: IAPDatePickerChangeEvent = {
          date: nextDate,
          formatted: nextDate ? format(nextDate, dateFormat) : '',
        };
        onChange?.(event);
      },
      [onChange, dateFormat]
    );

    const calculateMenuPosition = useCallback(() => {
      if (!containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      };
    }, []);

    const open = () => {
      if (disabled) return;
      setIsOpen(true);
      const position = calculateMenuPosition();
      setMenuPosition(position);
    };

    const close = () => {
      setIsOpen(false);
      setMenuPosition(null);
    };

    // Click outside and escape handling
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          close();
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          close();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleScrollOrResize = () => {
        const position = calculateMenuPosition();
        setMenuPosition(position);
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }, [isOpen, calculateMenuPosition]);

    const handleCalendarSelect = (date: Date) => {
      if (!disabled) {
        if (!value) {
          setInternalDate(date);
        }
        emitChange(date);
        close();
      }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (!raw) {
        if (!value) {
          setInternalDate(null);
        }
        emitChange(null);
        return;
      }

      const parsed = parse(raw, dateFormat, new Date());
      if (isValid(parsed)) {
        if (!value) {
          setInternalDate(parsed);
        }
        emitChange(parsed);
      }
    };

    const handleClear = () => {
      if (!value) {
        setInternalDate(null);
      }
      emitChange(null);
    };

    const displayLabel = texts?.label ?? textFieldProps.label;
    const displayPlaceholder = texts?.placeholder ?? textFieldProps.placeholder;

    const calendarMinDate = minDate;
    const calendarMaxDate = maxDate;

    const calendar = (
      <APCalendar
        month={month}
        selectedDate={selectedDate ?? undefined}
        onSelectDate={handleCalendarSelect}
        onMonthChange={(next) => setMonth(next)}
        minDate={calendarMinDate}
        maxDate={calendarMaxDate}
        {...calendarProps}
      />
    );

    const calendarIcon = (
      <APIconButton
        icon="calendar_line"
        size="small"
        type="button"
        variant="transparent"
        className="ap-date-picker__icon-button"
        aria-label={texts?.openCalendarAriaLabel ?? translate('aria.open-calendar')}
        onClick={() => (isOpen ? close() : open())}
        disabled={disabled}
      />
    );

    return (
      <div className="ap-date-picker" ref={containerRef}>
        <APTextField
          {...textFieldProps}
          ref={ref}
          label={displayLabel}
          placeholder={displayPlaceholder}
          value={formattedValue}
          onChange={handleTextChange}
          onClear={clearable ? handleClear : textFieldProps.onClear}
          clearable={clearable}
          disabled={disabled}
          rightIcon={
            <>
              {rightIcon}
              {calendarIcon}
            </>
          }
        />

        {isOpen && (
          <div
            ref={menuRef}
            className="ap-date-picker__popover"
            style={
              menuPosition
                ? {
                    position: 'fixed',
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                    width: `${menuPosition.width}px`,
                    zIndex: 999999,
                  }
                : undefined
            }
            role="dialog"
            aria-modal="false"
          >
            {calendar}
          </div>
        )}
      </div>
    );
  }
);

APDatePicker.displayName = 'APDatePicker';

export type { IAPDatePickerProps, IAPDatePickerChangeEvent };
export default APDatePicker;
